import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { PALETTE } from "../palette";
import type { FxInstance } from "./types";

/**
 * Cartoon-style effects per `04-art-direction.md`: "puffs of smoke, popping sludge
 * bubbles, exaggerated heat waves". Each component animates purely from
 * elapsed/duration so the FX layer can drop it on a timer without any
 * per-effect teardown logic.
 */

interface EffectProps {
  fx: FxInstance;
  /** 0..1 progress through the effect's duration. */
  progressRef: React.MutableRefObject<number>;
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

/** Expanding ring where two reagent streams combine. */
export function MergeFlash({ fx, progressRef }: EffectProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const t = easeOut(progressRef.current);
    const scale = 0.2 + t * 0.9;
    mesh.scale.set(scale, scale, scale);
    (mesh.material as THREE.MeshBasicMaterial).opacity = 1 - t;
  });
  return (
    <mesh ref={meshRef} position={fx.at} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[0.22, 0.3, 20]} />
      <meshBasicMaterial color={fx.color ?? PALETTE.fluidMagenta} transparent side={THREE.DoubleSide} />
    </mesh>
  );
}

/** A quick bright pop when a recipe fires. */
export function ReactionFlash({ fx, progressRef }: EffectProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const t = progressRef.current;
    const pop = Math.sin(t * Math.PI);
    mesh.scale.setScalar(0.15 + pop * 0.4);
    (mesh.material as THREE.MeshBasicMaterial).opacity = pop;
  });
  return (
    <mesh ref={meshRef} position={fx.at}>
      <sphereGeometry args={[1, 12, 12]} />
      <meshBasicMaterial color={fx.color ?? PALETTE.hazardWarning} transparent />
    </mesh>
  );
}

/** Exaggerated heat waves: stacked wobbling rings rising off a hot node. */
export function HeatShimmer({ fx, progressRef }: EffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  useFrame(({ clock }) => {
    const group = groupRef.current;
    if (!group) return;
    const t = progressRef.current;
    group.children.forEach((child, i) => {
      const offset = i * 0.18;
      child.position.y = offset + t * 0.5;
      child.position.x = Math.sin(clock.elapsedTime * 6 + i) * 0.05;
      const mesh = child as THREE.Mesh;
      (mesh.material as THREE.MeshBasicMaterial).opacity = (1 - t) * 0.5;
    });
  });
  return (
    <group ref={groupRef} position={fx.at}>
      {[0, 1, 2].map((i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.12 + i * 0.04, 0.16 + i * 0.04, 16]} />
          <meshBasicMaterial color={PALETTE.hazardWarning} transparent side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

/** Throbbing red halo warning that a node is about to let go. */
export function WarningPulse({ fx, progressRef }: EffectProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const throb = 0.5 + Math.sin(clock.elapsedTime * 12) * 0.5;
    mesh.scale.setScalar(0.55 + throb * 0.2);
    (mesh.material as THREE.MeshBasicMaterial).opacity = (1 - progressRef.current) * (0.35 + throb * 0.4);
  });
  return (
    <mesh ref={meshRef} position={fx.at}>
      <sphereGeometry args={[1, 14, 14]} />
      <meshBasicMaterial color={fx.color ?? PALETTE.hazardCritical} transparent />
    </mesh>
  );
}

/** Cartoon explosion: a fast flash plus a ring of tumbling debris chunks. */
export function Explosion({ fx, progressRef }: EffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  const debris = useMemo(
    () =>
      Array.from({ length: 9 }, (_, i) => {
        const angle = (i / 9) * Math.PI * 2;
        return {
          dir: new THREE.Vector3(Math.cos(angle), 0.9 + Math.random() * 0.6, Math.sin(angle)),
          spin: (Math.random() - 0.5) * 10,
        };
      }),
    [],
  );

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;
    const t = progressRef.current;
    group.children.forEach((child, i) => {
      if (i === 0) {
        // Flash core
        const pop = Math.max(0, 1 - t * 4);
        child.scale.setScalar(0.2 + pop * 1.2);
        ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = pop;
        return;
      }
      const spec = debris[i - 1];
      if (!spec) return;
      const travel = easeOut(t) * 1.3;
      child.position.set(
        spec.dir.x * travel,
        spec.dir.y * travel - t * t * 1.8, // gravity
        spec.dir.z * travel,
      );
      child.rotation.x += spec.spin * 0.02;
      child.rotation.z += spec.spin * 0.02;
      ((child as THREE.Mesh).material as THREE.MeshToonMaterial).opacity = 1 - t;
    });
  });

  return (
    <group ref={groupRef} position={fx.at}>
      <mesh>
        <sphereGeometry args={[1, 14, 14]} />
        <meshBasicMaterial color={fx.color ?? PALETTE.hazardSpark} transparent />
      </mesh>
      {debris.map((_, i) => (
        <mesh key={i} castShadow>
          <boxGeometry args={[0.11, 0.11, 0.11]} />
          <meshToonMaterial color={PALETTE.chassisGreyDark} transparent />
        </mesh>
      ))}
    </group>
  );
}

/** Pipe shaking under backlog pressure. */
export function PressureJudder({ fx, progressRef }: EffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  useFrame(({ clock }) => {
    const group = groupRef.current;
    if (!group) return;
    const intensity = (1 - progressRef.current) * 0.06;
    group.position.set(
      fx.at[0] + Math.sin(clock.elapsedTime * 55) * intensity,
      fx.at[1],
      fx.at[2] + Math.cos(clock.elapsedTime * 47) * intensity,
    );
  });
  return (
    <group ref={groupRef} position={fx.at}>
      <mesh>
        <torusGeometry args={[0.16, 0.03, 8, 16]} />
        <meshBasicMaterial color={PALETTE.hazardWarning} transparent opacity={0.8} />
      </mesh>
    </group>
  );
}

/** Fluid spraying out of a burst pipe and pooling on the tile. */
export function BurstSpill({ fx, progressRef }: EffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  const droplets = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const angle = (i / 7) * Math.PI * 2;
        return new THREE.Vector3(Math.cos(angle) * 0.8, 1.1, Math.sin(angle) * 0.8);
      }),
    [],
  );

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;
    const t = progressRef.current;
    group.children.forEach((child, i) => {
      if (i === 0) {
        // Spreading puddle
        const spread = easeOut(Math.min(t * 1.5, 1));
        child.scale.set(spread, spread, spread);
        ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = 0.7 * (1 - t * 0.4);
        return;
      }
      const dir = droplets[i - 1];
      if (!dir) return;
      child.position.set(dir.x * t * 0.9, Math.max(0, dir.y * t - t * t * 2.6), dir.z * t * 0.9);
      ((child as THREE.Mesh).material as THREE.MeshToonMaterial).opacity = 1 - t;
    });
  });

  return (
    <group ref={groupRef} position={fx.at}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.15, 0]}>
        <circleGeometry args={[0.55, 20]} />
        <meshBasicMaterial color={fx.color ?? PALETTE.fluidMagenta} transparent />
      </mesh>
      {droplets.map((_, i) => (
        <mesh key={i}>
          <sphereGeometry args={[0.06, 8, 8]} />
          <meshToonMaterial color={fx.color ?? PALETTE.fluidMagenta} transparent />
        </mesh>
      ))}
    </group>
  );
}

/** A brief rust bloom where corrosive reagent is eating the pipe. */
export function CorrosionMark({ fx, progressRef }: EffectProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const t = progressRef.current;
    mesh.scale.setScalar(0.3 + easeOut(t) * 0.4);
    (mesh.material as THREE.MeshBasicMaterial).opacity = Math.sin(t * Math.PI) * 0.75;
  });
  return (
    <mesh ref={meshRef} position={fx.at}>
      <sphereGeometry args={[0.3, 10, 10]} />
      <meshBasicMaterial color={PALETTE.terracottaDark} transparent />
    </mesh>
  );
}

/** Slow drip from a leaking pipe. */
export function LeakDribble({ fx, progressRef }: EffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;
    const t = progressRef.current;
    group.children.forEach((child, i) => {
      const phase = (t * 2 + i * 0.33) % 1;
      child.position.y = -phase * 0.5;
      child.scale.setScalar(1 - phase * 0.5);
      ((child as THREE.Mesh).material as THREE.MeshToonMaterial).opacity = (1 - phase) * (1 - t);
    });
  });
  return (
    <group ref={groupRef} position={fx.at}>
      {[0, 1, 2].map((i) => (
        <mesh key={i}>
          <sphereGeometry args={[0.05, 8, 8]} />
          <meshToonMaterial color={fx.color ?? PALETTE.fluidMagenta} transparent />
        </mesh>
      ))}
    </group>
  );
}

/** Sickly puff when impure feed degrades a catalyst bed. */
export function PoisonPuff({ fx, progressRef }: EffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;
    const t = progressRef.current;
    group.children.forEach((child, i) => {
      const spread = easeOut(t);
      const angle = (i / 5) * Math.PI * 2;
      child.position.set(Math.cos(angle) * spread * 0.4, spread * 0.5, Math.sin(angle) * spread * 0.4);
      child.scale.setScalar(0.12 + spread * 0.2);
      ((child as THREE.Mesh).material as THREE.MeshToonMaterial).opacity = (1 - t) * 0.8;
    });
  });
  return (
    <group ref={groupRef} position={fx.at}>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={i}>
          <sphereGeometry args={[1, 8, 8]} />
          <meshToonMaterial color={fx.color ?? PALETTE.sageDark} transparent />
        </mesh>
      ))}
    </group>
  );
}

/** Sludge bubbles swelling and popping in a stagnant node. */
export function SludgeBubble({ fx, progressRef }: EffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;
    const t = progressRef.current;
    group.children.forEach((child, i) => {
      const phase = (t * 1.6 + i * 0.4) % 1;
      // Swell, then pop: scale collapses abruptly past 0.8.
      const swell = phase < 0.8 ? phase / 0.8 : 1 - (phase - 0.8) / 0.2;
      child.scale.setScalar(0.05 + swell * 0.16);
      child.position.set((i - 1) * 0.14, phase * 0.25, 0);
      ((child as THREE.Mesh).material as THREE.MeshToonMaterial).opacity = (1 - t) * 0.9;
    });
  });
  return (
    <group ref={groupRef} position={fx.at}>
      {[0, 1, 2].map((i) => (
        <mesh key={i}>
          <sphereGeometry args={[1, 10, 10]} />
          <meshToonMaterial color={PALETTE.sageDark} transparent />
        </mesh>
      ))}
    </group>
  );
}

/** Dust ring kicked up where a hardware card lands on the board. */
export function CardSlam({ fx, progressRef }: EffectProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const t = easeOut(progressRef.current);
    mesh.scale.setScalar(0.3 + t * 1.1);
    (mesh.material as THREE.MeshBasicMaterial).opacity = (1 - t) * 0.6;
  });
  return (
    <mesh ref={meshRef} position={fx.at} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[0.3, 0.42, 22]} />
      <meshBasicMaterial color={PALETTE.creamDark} transparent side={THREE.DoubleSide} />
    </mesh>
  );
}

/** Rising tick when product lands in the hopper. */
export function ObjectiveTick({ fx, progressRef }: EffectProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const t = progressRef.current;
    mesh.position.y = fx.at[1] + easeOut(t) * 0.7;
    mesh.scale.setScalar(0.1 + Math.sin(t * Math.PI) * 0.12);
    (mesh.material as THREE.MeshBasicMaterial).opacity = 1 - t;
  });
  return (
    <mesh ref={meshRef} position={fx.at}>
      <octahedronGeometry args={[1, 0]} />
      <meshBasicMaterial color={fx.color ?? PALETTE.fluidMintGreen} transparent />
    </mesh>
  );
}
